import { useCallback, useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router";
import { Paper, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { apiGet } from "../utils";
import { buildLegacyAuditTrendUrl, hasDateFilters } from "../utils/filters";
import { InsightsPanel } from "../utils/panels";
import { LoadingPanel } from "../utils/loading_spinner";
import { createPlot } from "../utils/plotUtils";

const TREND_POCKET_MARGIN_CONFIG = [
    {
        valueField: "unit_price_avg",
        type: "scatter",
        mode: "lines",
        fill: "tonexty",
        fillcolor: `rgba(244, 195, 177, 0.6)`,
        line: (theme) => ({ color: theme.palette.primary.critical, width: 3 }),
        name: "Unit Price Avg",
        yaxis: "y",
        hovertemplate: (name) => `$${name}: $%{y:.2f}<extra></extra>`
    },
    {
        valueField: "unit_vcogs",
        type: "scatter",
        mode: "lines",
        fill: "tozeroy",
        fillcolor: `rgba(169, 169, 169, 0.6)`,
        line: (theme) => ({ color: theme.palette.primary.blue, width: 3 }),
        name: "Unit VCOGS",
        yaxis: "y",
        hovertemplate: (name) => `$${name}: $%{y:.2f}<extra></extra>`
    },
    {
        valueField: "unit_pocket_price_margin",
        type: "scatter",
        mode: "lines",
        fill: "tonexty",
        fillcolor: `rgba(161, 209, 255, 0.6)`,
        line: (theme) => ({ color: theme.palette.primary.main, width: 3 }),
        name: "Pocket Margin $/Unit",
        yaxis: "y",
        hovertemplate: (name) => `$${name}: $%{y:.2f}<extra></extra>`
    },

];

const TREND_POCKET_MARGIN_BY_CUSTOMER_CONFIG = [
    { 
        valueField: "existing_customer",
        type: "scatter",
        mode: "lines+markers",
        name: "Existing",
        line: (theme) => ({ color: theme.palette.status.success, width: 2 }),
        marker: { size: 5 }
    },
    {
        valueField: "new_customer",
        type: "scatter",
        mode: "lines+markers",
        name: "New",
        line: (theme) => ({ color: theme.palette.status.warning, width: 2 }),
        marker: { size: 5 }
    },
    {
        valueField: "lost_customer",
        type: "scatter",
        mode: "lines+markers",
        name: "Lost",
        line: (theme) => ({ color: theme.palette.status.critical, width: 2 }),
        marker: { size: 5 }
    }
]

const TREND_POCKET_MARGIN_SALES_REP = [ 
    {
        valueField: "pocket_price_margin",
        type: "scatter", 
        name: "Pocket Margin $",
        yaxis: "y2",
        hovertemplate: (name) => `$${name}: $%{y:.2f}<extra></extra>`
    },
    {
        valueField: "sales_rep",
        type: "bar",
        text: (arr) => arr,
        texttemplate: "%{y}",
        textfont: { size:10 },      
        name: "Sales Rep",
        yaxis: "y",
    }, 
]

const TREND_PRODUCT_BELOW_TARGET = [
    {
        valueField: "product_count",
        type: "bar",
        mode: "lines",
        text: (arr) => arr,
        texttemplate: "%{y}",
        textfont: { size:10 },
        name: "Products",
        yaxis: "y",
    }, 
   {
        valueField: "revenue",
        type: "scatter",
        name: "Revenue",
        yaxis: "y2",
        hovertemplate: (name) => `$${name}: $%{y:.2f}<extra></extra>`
    },
]
const getMonthName = (monthNumber) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNumber - 1] ?? null;
}

export const AuditTrendsContainer = () => {
    const { filters } = useOutletContext();
    const theme = useTheme();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [pocketMarginData, setPocketMarginData] = useState([]);
    const [pocketMarginByCustomerData, setPocketMarginByCustomerData] = useState([]);
    const [pocketMarginBySalesRepData, setPocketMarginBySalesRepData] = useState([]);
    const [productsBelowTarget, setProductsBelowTarget] = useState([]);
    const [loading, setLoading] = useState(false);

    const convertPlotData = useCallback((traceData, configs) => {

        const traceYears = [...new Set(traceData.map(i => i.year))];
        let xData; 
        if (traceData[0].day) {
            xData = traceData.map(i => {
                const y = `${traceYears.length > 1 ? i.year + '/' : ''}`;
                const m = `${i.month}`.padStart(2, '0');
                const d = `${i.day}`.padStart(2, '0');
                return `${y}${m}/${d}`;
            });
        } else if (traceData[0].month) {
            xData = traceData.map(i => {
                return getMonthName(i.month);
            });
        } else if (traceData[0].quarter) {
            xData = traceData.map(i => {
                const y = `${traceYears.length > 1 ? i.year + '/' : ''}`;
                const q = `Q${i.quarter}`;
                return `${y}${q}`;
            });
        } else if (traceData[0].year) {
            xData = traceData.map(i => i.year);
        }
        return configs.map(config => {  
            const d = {
                x: xData,
                y: traceData.map(i => (i[config.valueField]) || 0), 
                type: config.type || "scatter",
                yaxis: "y",
            };
            if(config.mode) d['mode'] = config.mode;
            if(config.fill) d['fill'] = config.fill;
            if(config.fillcolor) d['fillcolor'] = config.fillcolor;
            if(config.line) d['line'] = config.line(theme);
            if(config.name) d['name'] = config.name;
            if(config.yaxis) d['yaxis'] = config.yaxis;
            if(config.textposition) d['textposition'] = config.textposition;
            if(config.texttemplate) d['texttemplate'] = config.texttemplate;
            if(config.textfont) d['textfont'] = config.textfont;
            if(config.opacity) d['opacity'] = config.opacity;
            if(config.text){
                if(typeof(config.text) == 'function') d['text'] = config.text(d.y);
                else d['text'] = config.text;
            }
            if(config.hovertemplate) d['hovertemplate'] = config.hovertemplate;
            return d; 
        })
    }, [theme]);

    const fetchAuditTrendsPocketMargin = useCallback(() => {
        if (!hasDateFilters(filters)) return;

        setLoading(true);
        const url = buildLegacyAuditTrendUrl('trends/pocket_margin', filters);

        apiGet(url, (resp) => {
            if (resp?.success) { 
                if( resp.count === 0 ) setPocketMarginData([]);
                else setPocketMarginData(convertPlotData(resp.data, TREND_POCKET_MARGIN_CONFIG));
            }
            setLoading(false);
        });
    }, [filters, convertPlotData]);

    
    const fetchAuditTrendsPocketMarginByCustomer = useCallback(() => {
        if (!hasDateFilters(filters)) return;

        setLoading(true);
        const url = buildLegacyAuditTrendUrl('trends/pocket_margin_by_customer', filters);

        apiGet(url, (resp) => {
            if (resp?.success) {  
                if( resp.count === 0 ) setPocketMarginByCustomerData([]);
                else setPocketMarginByCustomerData(convertPlotData(resp.data, TREND_POCKET_MARGIN_BY_CUSTOMER_CONFIG));
            }
            setLoading(false);
        });
    }, [filters, convertPlotData]);

    const fetchAuditTrendsPocketMarginBySalesRep = useCallback(() => {
        if (!hasDateFilters(filters)) return;
        setLoading(true);
        const url = buildLegacyAuditTrendUrl('trends/pocket_margin_and_sales_rep', filters);
 
        apiGet(url, (resp) => {
            if (resp?.success) { 
                if( resp.count === 0 ) setPocketMarginBySalesRepData([]);
                else setPocketMarginBySalesRepData(convertPlotData(resp.data, TREND_POCKET_MARGIN_SALES_REP) || []);
            }
            setLoading(false);
        });
    }, [filters, convertPlotData]);
    
    const fetchAuditTrendsProductsBelowTargetVariableMargin = useCallback(() => {
        if (!hasDateFilters(filters)) return;
        setLoading(true);
        const url = buildLegacyAuditTrendUrl('trends/product_below_target', filters);

        apiGet(url, (resp) => {
            if (resp?.success) { 
                if( resp.count === 0 ) setProductsBelowTarget([]);
                else setProductsBelowTarget(convertPlotData(resp.data, TREND_PRODUCT_BELOW_TARGET) || []);
            }
            setLoading(false);
        });
    }, [filters, convertPlotData]);

    const fetchAuditTrends = useCallback(() => {
        if (!hasDateFilters(filters)) return;

        setLoading(true);
        const url = buildLegacyAuditTrendUrl('trends', filters);

        apiGet(url, (resp) => {
            if (resp?.success) {
                setData(resp.data);
            }
            setLoading(false);
        });
    }, [filters]);

    useEffect(() => {
        fetchAuditTrends();
    }, [fetchAuditTrends]);

    useEffect(() => {
        fetchAuditTrendsPocketMargin();
    }, [fetchAuditTrendsPocketMargin]);

    useEffect(() => {
        fetchAuditTrendsPocketMarginByCustomer();
    }, [fetchAuditTrendsPocketMarginByCustomer]);

    useEffect(() => {
        fetchAuditTrendsPocketMarginBySalesRep();
    }, [fetchAuditTrendsPocketMarginBySalesRep]);

    useEffect(() => {
        fetchAuditTrendsProductsBelowTargetVariableMargin();
    }, [fetchAuditTrendsProductsBelowTargetVariableMargin]);

    if (loading) {
        return <LoadingPanel loading={true} message="Loading audit trends..." />;
    }

    if (!pocketMarginData || !pocketMarginByCustomerData || !pocketMarginBySalesRepData || !data ) {
        return (
            <Paper style={{ ...theme.palette.contentPanel, padding: 40, margin: 30 }}>
                <Typography align="center">No data available for selected filters</Typography>
            </Paper>
        );
    }

    const ChartCard = ({ title, children, insights, rootCause, kpiLink }) => (
        <Paper style={{ ...theme.palette.contentPanel, padding: 20 }}>
            <Typography 
                variant="h6" 
                style={{
                    color: theme.palette.primary.main,
                    fontSize: 14,
                    fontWeight: "bold",
                    marginBottom: 6,
                    cursor: kpiLink ? "pointer" : "default",
                    transition: "all 0.2s ease",
                    display: "inline-block"
                }}
                onMouseEnter={(e) => {
                    if (kpiLink) {
                        e.target.style.textDecoration = "underline";
                        e.target.style.opacity = "0.8";
                    }
                }}
                onMouseLeave={(e) => {
                    if (kpiLink) {
                        e.target.style.textDecoration = "none";
                        e.target.style.opacity = "1";
                    }
                }}
                onClick={() => kpiLink && navigate(`/reports/kpi?kpi=${kpiLink}`)}
            >
                {title}
            </Typography>
            {children}
            {insights && <InsightsPanel is_insight={true} content={insights} sx={{ mt: 2 }} />}
            {rootCause && <InsightsPanel is_insight={false} content={rootCause} sx={{ mt: 1 }} />}
        </Paper>
    );

    return (
        <div style={{ margin: 30, marginBottom: 100 }}>
            <Grid container spacing={3}>
                <Grid size={4}>
                    <ChartCard 
                        title="Pocket Margin $/Unit"
                        kpiLink="unit_pocket_price_margin"
                        insights="Contribution margin/unit decreased by $25/unit over the past 90 days. Estimated margin leak is $620K"
                        rootCause="Most of the decrease is driven by sales overrides outside approved bands."
                    >
                        {createPlot(
                            pocketMarginData, {
                            yaxis: {
                                title: "Unit Price Avg",
                                side: "left",
                                showgrid: true,
                                tickformat: "$,.0f"
                            },
                            yaxis2: {
                                title: "Unit Variable Margin",
                                side: "right",
                                overlaying: "y",
                                showgrid: false,
                                tickformat: "$,.0f"
                            },
                            xaxis: {
                                title: ""
                            },
                            showlegend: true,
                            legend: { 
                                orientation: "h",
                                y: 1.15,
                                x: 0,
                                bgcolor: "rgba(255,255,255,0.8)"
                            },
                            margin: { t: 30, b: 30, l: 30, r: 30 }
                        })}
                    </ChartCard>
                </Grid>

                <Grid size={4}>
                    <ChartCard 
                        title="Profits from Existing/New/Lost Customer Accounts"
                        kpiLink="pocket_price_margin"
                        insights="Contribution margin/unit decreased by $25/unit over the past 90 days. Estimated margin leak is $620K"
                        rootCause="Most of the decrease is driven by sales overrides outside approved bands."
                    >
                        {createPlot(
                            pocketMarginByCustomerData, {
                            yaxis: { title: "Pocket Margin ($)" },
                            showlegend: true,
                            legend: { 
                                orientation: "h", 
                                y: 1.15,
                                x: 0,
                                bgcolor: "rgba(255,255,255,0.8)"
                            },
                            margin: { t: 30, b: 30, l: 40, r: 20 }
                        })}
                    </ChartCard>
                </Grid>

                <Grid size={4}>
                    <ChartCard 
                        title="Pocket Margin / Sales Rep"
                        insights="Contribution margin/unit decreased by $25/unit over the past 90 days. Estimated margin leak is $620K"
                        rootCause="Most of the decrease is driven by sales overrides outside approved bands."
                    >
                        {createPlot( 
                            pocketMarginBySalesRepData, { 
                            yaxis: {
                                title: "Pocket Price Margin($)",
                                side: "right",
                                showgrid: true,  
                                tickformat: "~s",    
                                layer: "above traces"
                            },
                            yaxis2: {
                                title: "Sales Rep",
                                side: "left",
                                showgrid: false,
                                layer: "below traces"
                            },
                            legend: { 
                                orientation: "h", 
                                y: 1.15,
                                x: 0,
                                bgcolor: "rgba(255,255,255,0.8)"
                            },
                            margin: { t: 30, b: 30, l: 30, r: 30 }
                        })}
                    </ChartCard>
                </Grid>

                <Grid size={4}>
                    <ChartCard 
                        title="# Products & Revenues below Target VM %"
                        insights="Contribution margin/unit decreased by $25/unit over the past 90 days. Estimated margin leak is $620K"
                        rootCause="Most of the decrease is driven by sales overrides outside approved bands."
                    >
                        {createPlot(
                            productsBelowTarget, { 
                            yaxis: {
                                title: "Products",
                                side: "left",
                                showgrid: true,    
                            },
                            yaxis2: {
                                title: "Revenue",
                                side: "right",  
                                showgrid: false,  
                                tickformat: "~s",  
                            },  
                            legend: { 
                                orientation: "h", 
                                y: 1.15,
                                x: 0,
                                bgcolor: "rgba(255,255,255,0.8)"
                            },
                            margin: { t: 30, b: 30, l: 30, r: 30 }
                        })}
                    </ChartCard>
                </Grid>

                <Grid size={4}>
                    <ChartCard 
                        title="PPM$ Leak by Time of Last Purchase"
                        insights="Contribution margin/unit decreased by $25/unit over the past 90 days. Estimated margin leak is $620K"
                        rootCause="Most of the decrease is driven by sales overrides outside approved bands."
                    >
                        {createPlot([
                            {
                                x: data.ppmsLeaks?.timeRanges || [],
                                y: data.ppmsLeaks?.northeast || [],
                                type: "bar",
                                name: "Northeast",
                                width: 0.4,
                                marker: { color: theme.palette.primary.main }
                            },
                            {
                                x: data.ppmsLeaks?.timeRanges || [],
                                y: data.ppmsLeaks?.southwest || [],
                                type: "bar",
                                name: "Southwest",
                                width: 0.4,
                                marker: { color: theme.palette.status.warning }
                            },
                            {
                                x: data.ppmsLeaks?.timeRanges || [],
                                y: data.ppmsLeaks?.midwest || [],
                                type: "bar",
                                name: "Midwest",
                                width: 0.4,
                                marker: { color: theme.palette.status.neutral }
                            }
                        ], { 
                            barmode: "stack", 
                            yaxis: { title: "Pocket Margin ($)" },
                            showlegend: true,
                            legend: { 
                                orientation: "h", 
                                y: 1.15,
                                x: 0,
                                bgcolor: "rgba(255,255,255,0.8)"
                            },
                            margin: { t: 30, b: 30, l: 40, r: 20 }
                        })}
                    </ChartCard>
                </Grid>

                <Grid size={4}>
                    <ChartCard 
                        title="# Price Exceptions to List Price"
                        insights="Contribution margin/unit decreased by $25/unit over the past 90 days. Estimated margin leak is $620K"
                        rootCause="Most of the decrease is driven by sales overrides outside approved bands."
                    >
                        {createPlot([{
                            x: data.priceExceptions?.months || [],
                            y: data.priceExceptions?.exceptions || [],
                            type: "bar",
                            marker: { color: theme.palette.heatmap.critical.medium }
                        }], { yaxis: { title: "Count" } })}
                    </ChartCard>
                </Grid>
            </Grid>
        </div>
    );
};